import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Loader2 } from 'lucide-react'
import { proceduresApi } from '@/api/proceduresApi'
import { equipmentApi } from '@/api/equipmentApi'
import { CleaningTaskBoardCard } from '@/components/board/CleaningTaskBoardCard'
import { useAssignableUsers } from '@/hooks/useAssignableUsers'
import { useAuth } from '@/providers/AuthProvider'
import { useTenant } from '@/providers/TenantProvider'
import { buildBoardTasks, expiredRunIds, type BoardTask } from '@/lib/board/boardTasks'
import { BoardHistoryPanel, BoardProductsDialog, RunAuditTrailDialog } from '@/components/board/BoardRunDialogs'
import { BoardCardSettings, BoardFilterBar } from '@/components/board/BoardFilterBar'
import { BoardReserveFlow } from '@/components/board/BoardReserveFlow'
import { ProcedureBoardCard, type BoardCardAccess } from '@/components/board/ProcedureBoardCard'
import { useBackNavLinkBuilder } from '@/components/navigation/BackLink'
import { Button } from '@/components/ui/button'
import { useBoardPrefs } from '@/hooks/useBoardPrefs'
import { useKitForms, useKitInventory } from '@/hooks/usePickLists'
import {
  useBoardCleaningTasks,
  useBoardPatients,
  useInvalidateProcedureBoard,
  useProcedureBoard,
} from '@/hooks/useProcedureBoard'
import { useProcedureAssignableUsers } from '@/hooks/useProcedureAssignableUsers'
import { useCustomersList } from '@/hooks/useCustomers'
import { CUSTOMER_CATEGORY } from '@/lib/customers'
import { useInvalidateStepExecutions } from '@/hooks/useProcessSteps'
import { compareBoardCards, effectiveColumns } from '@/lib/board/boardCardView'
import {
  DEFAULT_BOARD_FILTERS,
  boardSearchFor,
  parseBoardSearch,
  sameBoardFilters,
  type BoardFilters,
} from '@/lib/board/boardFilters'
import { buildBoardRuns, type BoardRun } from '@/lib/board/boardRuns'
import { patientName } from '@/lib/patient'
import { PERMISSIONS as P, usePermission } from '@/lib/permissions'
import { localTodayISO } from '@/lib/procedureDates'
import { kitProgressByProcedure } from '@/lib/procedureStepStatus'
import { adHocStepQuery, procedureRunPath, withQuery } from '@/lib/procedureRunPath'
import { requestErrorMessage } from '@/lib/requestErrorMessage'
import { PageHeading } from '@/components/layout/PageHeading'

/** Cards added per "Load more". */
const PAGE = 40

/** A card on the board: a procedure run, or a cleaning task with no run yet (z8nrz7f9u5). */
type BoardItem = { kind: 'run'; key: string; run: BoardRun } | { kind: 'task'; key: string; task: BoardTask }

/** The board's order (`compareBoardCards`) over both kinds of card. */
function orderKey(item: BoardItem): { dueDate: string | null; startedAt: string; id: string } {
  if (item.kind === 'run') return item.run.procedure
  return { dueDate: item.task.dueDate, startedAt: item.task.event.createdAt ?? '', id: item.task.event.id }
}

/**
 * Overall Status: every open procedure, of every type, as a card.
 *
 * Applied filters live in the url, so a title icon can link to one type and a
 * filtered board can be shared or reloaded. The controls edit a draft that Apply
 * applies. Card layout and visible elements are the user's own settings.
 */
export function OverallStatusPage() {
  const navigate = useNavigate()
  const withOrigin = useBackNavLinkBuilder('Overall Status')
  const [params, setParams] = useSearchParams()
  const search = params.toString()
  const applied = useMemo(() => parseBoardSearch(new URLSearchParams(search)), [search])
  const [draft, setDraft] = useState<BoardFilters>(applied)
  useEffect(() => setDraft(applied), [applied])

  const { can } = usePermission()
  const canAssign = can(P.proceduresAssign)
  const canManage = can(P.proceduresManage)
  const canExecute = can(P.processExecute)

  const board = useProcedureBoard(applied)
  const [today] = useState(localTodayISO())
  // Cleaning tasks beside the runs (z8nrz7f9u5): read with the Tasks right,
  // assigned with the right the Tasks row and Edit dialog need.
  const { selectedTenantId } = useTenant()
  const { email, user } = useAuth()
  const queryClient = useQueryClient()
  const canAssignTasks = can(P.equipmentExecute)
  const cleaningTasks = useBoardCleaningTasks(applied, today, can(P.equipmentRead))
  const { users: taskUsers } = useAssignableUsers()
  const patientsQuery = useBoardPatients()
  // The kit feeds need inventory.view; without it kit cards use step progress.
  const includesKits =
    can(P.inventoryView) && (applied.types.length === 0 || applied.types.includes('KitCreation'))
  const kitForms = useKitForms(includesKits)
  const kitInventory = useKitInventory(includesKits)
  const { users } = useProcedureAssignableUsers(canAssign)
  // The Customer filter, for someone who can read the list (z8nrz7e71k).
  // The list of names a customer is picked from: the partners in the Customer
  // category (z8nrz7earp). A run already linked to another partner keeps
  // showing its name, from the run itself.
  const customersQuery = useCustomersList({ enabled: can(P.customersRead), categories: CUSTOMER_CATEGORY })
  const customerOptions = useMemo(
    () => (customersQuery.data ?? []).map(c => ({ value: c.id, label: `${c.code} — ${c.name}` })),
    [customersQuery.data],
  )
  const { prefs, setColumns, setShown, reset: resetPrefs } = useBoardPrefs()
  const invalidateBoard = useInvalidateProcedureBoard()
  const invalidateStepExecutions = useInvalidateStepExecutions()

  const nameByMrn = useMemo(() => {
    const map = new Map<string, string>()
    for (const pt of patientsQuery.data ?? []) {
      const name = patientName(pt)
      if (name) map.set(pt.mrn, name)
    }
    return map
  }, [patientsQuery.data])

  const patientOptions = useMemo<[string, string][]>(
    () =>
      (patientsQuery.data ?? [])
        .map(pt => {
          const name = patientName(pt)
          return [pt.mrn, name ? `${name} (${pt.mrn})` : pt.mrn] as [string, string]
        })
        .sort((a, b) => a[1].localeCompare(b[1])),
    [patientsQuery.data],
  )

  // In-progress kits first, finished second: a run in both feeds has just
  // finished, and the finished record is the truthful one.
  // One row per KIT, several per run (z8nrz7e11d): rolled up to the run.
  const kitByProcedureId = useMemo(
    () => kitProgressByProcedure([...(kitForms.data ?? []), ...(kitInventory.data ?? [])]),
    [kitForms.data, kitInventory.data],
  )

  const expiredRuns = useMemo(() => expiredRunIds(cleaningTasks.data ?? [], today), [cleaningTasks.data, today])
  const runs = useMemo(
    () =>
      buildBoardRuns(board.data?.items ?? [], {
        nameByMrn,
        kitByProcedureId,
        status: applied.status,
        expiredRunIds: expiredRuns,
      }),
    [board.data, nameByMrn, kitByProcedureId, applied.status, expiredRuns],
  )
  const tasks = useMemo(
    () => buildBoardTasks(cleaningTasks.data ?? [], applied, today),
    [cleaningTasks.data, applied, today],
  )
  const items = useMemo<BoardItem[]>(
    () =>
      [
        ...runs.map((run): BoardItem => ({ kind: 'run', key: run.procedure.id, run })),
        ...tasks.map((task): BoardItem => ({ kind: 'task', key: `task:${task.event.id}`, task })),
      ].sort((a, b) => compareBoardCards(orderKey(a), orderKey(b))),
    [runs, tasks],
  )

  const [limit, setLimit] = useState(PAGE)
  useEffect(() => setLimit(PAGE), [search])

  const gridRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = gridRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const columns = effectiveColumns(prefs.columns, width)

  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [assigningId, setAssigningId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [reserveRun, setReserveRun] = useState<BoardRun | null>(null)
  const [productsRun, setProductsRun] = useState<BoardRun | null>(null)
  const [historyRun, setHistoryRun] = useState<BoardRun | null>(null)
  const [auditRun, setAuditRun] = useState<BoardRun | null>(null)

  function applyFilters(next: BoardFilters) {
    setParams(boardSearchFor(next))
  }

  function resetFilters() {
    setDraft(DEFAULT_BOARD_FILTERS)
    setParams(new URLSearchParams())
  }

  function runPath(run: BoardRun): string | null {
    return procedureRunPath(run.type, run.procedure.id, run.procedure.patientMrn)
  }

  function openRun(run: BoardRun) {
    const path = runPath(run)
    if (path) navigate(withOrigin(path))
  }

  function openCapa(run: BoardRun) {
    const path = runPath(run)
    const submissionId = run.procedure.summary?.capa.latestSubmissionId
    if (path && submissionId) navigate(withOrigin(withQuery(path, adHocStepQuery(submissionId))))
  }

  async function assign(run: BoardRun, assigneeId: string | null) {
    const current = run.procedure.assignedTo?.kind === 'user' ? run.procedure.assignedTo.user.id : null
    if (current === assigneeId && run.procedure.assignedTo?.kind !== 'multiple') return
    setAssigningId(run.procedure.id)
    setActionError(null)
    try {
      await proceduresApi.assignProcedure(run.procedure.id, assigneeId)
      await invalidateBoard()
      // Step cards elsewhere read their own cached query.
      invalidateStepExecutions()
    } catch (e) {
      setActionError(requestErrorMessage(e, 'Failed to assign the procedure.'))
    } finally {
      setAssigningId(null)
    }
  }

  async function assignTask(task: BoardTask, assigneeId: string | null) {
    if (!selectedTenantId) return
    setAssigningId(task.event.id)
    setActionError(null)
    try {
      await equipmentApi.updateEvent(selectedTenantId, task.event.id, { assigneeId: assigneeId ?? '', actor: email ?? undefined })
      await queryClient.invalidateQueries({ queryKey: ['maintenance-events'] })
    } catch (e) {
      setActionError(requestErrorMessage(e, 'Failed to assign the cleaning task.'))
    } finally {
      setAssigningId(null)
    }
  }

  function accessFor(run: BoardRun): BoardCardAccess {
    const scheduled = !!run.procedure.scheduledDate
    const canReserve = canExecute && (scheduled || canManage)
    return {
      canAssign,
      canReserve,
      reserveBlockedReason: !canExecute
        ? 'You do not have permission to reserve.'
        : !scheduled && !canManage
          ? 'Set a scheduled start on this procedure first.'
          : null,
      canViewProducts: can(P.productsView),
      canViewCapa: can(P.capaView),
      // The trail lists the run's forms first (submissions.read), then each trail (audit.read).
      canReadAudit: can(P.auditRead) && can(P.submissionsRead),
    }
  }

  const visible = items.slice(0, limit)
  const dirty = !sameBoardFilters(draft, applied)

  return (
    <div className='min-h-full bg-muted/20'>
      <div className='px-6 py-8'>

        <div className='mb-6 flex flex-wrap items-center justify-between gap-4'>
          <PageHeading title='Overall Status' description='Every open procedure across Transplant, KIT Creation, MES, Inventory and Cleaning.' />
          <div className='flex items-center gap-2'>
            <BoardCardSettings prefs={prefs} onColumns={setColumns} onShown={setShown} onReset={resetPrefs} />
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                void board.refetch()
                void cleaningTasks.refetch()
              }}
              disabled={board.isFetching || cleaningTasks.isFetching}
            >
              {board.isFetching || cleaningTasks.isFetching ? <Loader2 className='h-4 w-4 animate-spin' /> : 'Refresh'}
            </Button>
          </div>
        </div>

        <BoardFilterBar
          draft={draft}
          onDraftChange={setDraft}
          patients={patientOptions}
          users={users}
          showAssignee={canAssign}
          customers={customerOptions}
          dirty={dirty}
          onGo={() => applyFilters(draft)}
          onReset={resetFilters}
        />

        {actionError && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {actionError}
          </div>
        )}

        <div ref={gridRef}>
          {board.isError ? (
            <div className='flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
              <span className='flex items-center gap-2'>
                <AlertCircle className='h-4 w-4 shrink-0' />
                {requestErrorMessage(board.error, 'The procedures could not be loaded.')}
              </span>
              <Button variant='outline' size='sm' onClick={() => void board.refetch()}>
                Retry
              </Button>
            </div>
          ) : board.isLoading ? (
            <div className='grid gap-4' style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
              {Array.from({ length: columns * 2 }, (_, i) => (
                <div key={i} className='h-52 animate-pulse rounded-lg border bg-muted/40' />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className='rounded-lg border bg-background px-6 py-12 text-center'>
              <p className='text-sm text-muted-foreground'>No procedures match these filters.</p>
              <Button variant='outline' size='sm' className='mt-3' onClick={resetFilters}>
                Reset filters
              </Button>
            </div>
          ) : (
            <>
              <p className='mb-3 text-xs text-muted-foreground'>
                {runs.length} {runs.length === 1 ? 'procedure' : 'procedures'}
                {tasks.length > 0 && ` · ${tasks.length} cleaning ${tasks.length === 1 ? 'task' : 'tasks'} not yet opened`}
              </p>
              <div className='grid gap-4' style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                {visible.map(item =>
                  item.kind === 'task' ? (
                    <CleaningTaskBoardCard
                      key={item.key}
                      task={item.task}
                      users={taskUsers}
                      canAssign={canAssignTasks}
                      meId={user?.userId}
                      assigning={assigningId === item.task.event.id}
                      onAssign={assigneeId => void assignTask(item.task, assigneeId)}
                      onOpen={() => navigate(withOrigin('/cleaning?tab=my-list'))}
                    />
                  ) : (
                    <ProcedureBoardCard
                      key={item.key}
                      run={item.run}
                      prefs={prefs}
                      access={accessFor(item.run)}
                      users={users}
                      assigning={assigningId === item.run.procedure.id}
                      today={today}
                      menuOpen={openMenuId === item.run.procedure.id}
                      onMenuOpenChange={open => setOpenMenuId(open ? item.run.procedure.id : null)}
                      onAssign={assigneeId => void assign(item.run, assigneeId)}
                      onOpen={() => openRun(item.run)}
                      onReserve={() => setReserveRun(item.run)}
                      onProducts={() => setProductsRun(item.run)}
                      onHistory={() => setHistoryRun(item.run)}
                      onCapa={() => openCapa(item.run)}
                      onAudit={() => setAuditRun(item.run)}
                    />
                  ),
                )}
              </div>
              {items.length > visible.length && (
                <div className='mt-6 flex justify-center'>
                  <Button variant='outline' onClick={() => setLimit(l => l + PAGE)}>
                    Load more ({visible.length} of {items.length})
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <BoardReserveFlow run={reserveRun} onClose={() => setReserveRun(null)} />
      <BoardProductsDialog run={productsRun} onClose={() => setProductsRun(null)} onOpenRun={openRun} />
      <BoardHistoryPanel run={historyRun} onClose={() => setHistoryRun(null)} />
      <RunAuditTrailDialog run={auditRun} onClose={() => setAuditRun(null)} />
    </div>
  )
}
